import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mic, MicOff, Users, Activity, Copy, Check, RefreshCw, XCircle, Loader2, Clock, BarChart3, Volume2, BookOpen, Plus, Trash2, ExternalLink, Monitor, Languages, CalendarDays, FileText, Download, History, FileDown, Radio, Play, Pause, Square, Signal, ChevronDown, ChevronRight, FlaskConical, LayoutGrid, Shield, Home, LogOut, QrCode, ArrowLeft, Gauge } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { GlossaryLinkCard } from '@/components/GlossaryLinkCard';
import { AccountWarningBanner } from '@/components/AccountWarningBanner';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

import { LANGUAGES, syncLanguages } from '../lib/languagesCatalog';

const TTS_VOICES = [
  { id: 'nova', name: 'Nova', desc: 'Feminina, suave' },
  { id: 'alloy', name: 'Alloy', desc: 'Neutra, versátil' },
  { id: 'echo', name: 'Echo', desc: 'Masculina, clara' },
  { id: 'onyx', name: 'Onyx', desc: 'Masculina, grave' },
  { id: 'fable', name: 'Fable', desc: 'Feminina, expressiva' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Feminina, calorosa' },
];

export default function HostConsole() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [mySessions, setMySessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [sourceLang, setSourceLang] = useState('pt');
  const [sessionName, setSessionName] = useState('');
  const [targetLangs, setTargetLangs] = useState(['pt']);
  const [bilateral, setBilateral] = useState(false);
  const [spokenLangs, setSpokenLangs] = useState(['pt', 'en']);
  const [sourceLangMode, setSourceLangMode] = useState('auto');
  const [forcedSourceLang, setForcedSourceLang] = useState(null);
  const [detectedLang, setDetectedLang] = useState(null);
  const [liveLagMs, setLiveLagMs] = useState(null);
  const [ttsRate, setTtsRate] = useState(null);
  const [ttsRateBusy, setTtsRateBusy] = useState(false);
  const [translationAudioMode, setTranslationAudioMode] = useState('quality');
  const [azureVoices, setAzureVoices] = useState([]);
  const [azureVoicePrefs, setAzureVoicePrefs] = useState({});
  const [azurePreviewLang, setAzurePreviewLang] = useState(null);
  const [captationPaused, setCaptationPaused] = useState(false);
  const [captationBusy, setCaptationBusy] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [accountWarnings, setAccountWarnings] = useState([]);
  const [subtitleCount, setSubtitleCount] = useState(0);
  const [latencyHistory, setLatencyHistory] = useState([]);
  const [avgLatency, setAvgLatency] = useState(0);
  const [lastText, setLastText] = useState('');
  const [glossary, setGlossary] = useState([]);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [participantEvents, setParticipantEvents] = useState([]);
  const [uniqueCount, setUniqueCount] = useState(0);
  const [sessionSettings, setSessionSettings] = useState({ teleprompter_enabled: true, interpreter_enabled: true });
  const [scheduledFor, setScheduledFor] = useState('');
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptCopied, setTranscriptCopied] = useState(false);
  const [audioSource, setAudioSource] = useState('mic');
  const [translationMode, setTranslationMode] = useState('openai');
  const [azureAvailable, setAzureAvailable] = useState(false);
  const [rtmpUrl, setRtmpUrl] = useState('');
  const [rtmpStreamKey, setRtmpStreamKey] = useState('');
  const [rtmpWorkerRunning, setRtmpWorkerRunning] = useState(false);
  const [rtmpStreamActive, setRtmpStreamActive] = useState(false);
  const [rtmpCopied, setRtmpCopied] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState('');
  const [speakerInput, setSpeakerInput] = useState('');
  const [rtmpStreamQuality, setRtmpStreamQuality] = useState(null);
  const [ttsVoice, setTtsVoice] = useState('nova');
  const [avOverlayOpen, setAvOverlayOpen] = useState(false);
  const [avOverlayLang, setAvOverlayLang] = useState('');
  const [avCopied, setAvCopied] = useState(null);
  const [participantControlsOpen, setParticipantControlsOpen] = useState(false);
  const [ttsVoiceOpen, setTtsVoiceOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [vadSpeaking, setVadSpeaking] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const previewAudioRef = useRef(null);
  const transcriptEndRef = useRef(null);
  
  const [gainValue, setGainValue] = useState(() => {
    const saved = localStorage.getItem('host_audio_gain');
    return saved ? parseFloat(saved) : 1.0;
  });
  
  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const gainNodeRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      toast.error('Por favor, faça login para criar sessões');
      navigate('/login');
      return;
    }
    
    setCurrentUser(JSON.parse(user));
    fetchMySessions(token);
    // SaaS S2: fetch account warnings to surface in the host header
    axios.get(`${API}/me/account`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAccountWarnings(res.data.warnings || []))
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    axios.get(`${API}/config/translation-modes`).then(res => {
      setAzureAvailable(res.data.azure_available || false);
    }).catch(() => {});
    const token = localStorage.getItem('token');
    axios.get(`${API}/config/azure-voices`, { headers: { Authorization: `Bearer ${token}` } }).then(res => {
      if (res.data.available) setAzureVoices(res.data.voices || []);
    }).catch(() => {});
  }, []);

  const fetchMySessions = async (token) => {
    try {
      const authToken = token || localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/my-sessions?include_archived=true`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setMySessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const resumeSession = (existingSession) => {
    setSession(existingSession);
    setGlossary(existingSession.glossary || []);
    setSessionSettings(existingSession.settings || { teleprompter_enabled: true, interpreter_enabled: true });
    setAudioSource(existingSession.audio_source || 'mic');
    setTtsVoice(existingSession.tts_voice || 'nova');
    setAzureVoicePrefs(existingSession.azure_voice_prefs || {});
    setCaptationPaused(!!existingSession.captation_paused);
    setSessionName(existingSession.name || '');
    setCurrentSpeaker(existingSession.current_speaker || '');
    setSpeakerInput(existingSession.current_speaker || '');
    if (existingSession.audio_source === 'rtmp') {
      setRtmpWorkerRunning(true);
      // Build RTMP URL from domain
      const domain = window.location.hostname;
      setRtmpUrl(`rtmp://${domain}:1935/live/${existingSession.code}`);
      setRtmpStreamKey(existingSession.code);
    } else {
      setRtmpWorkerRunning(false);
      setRtmpStreamActive(false);
    }
    toast.success('Sessão retomada!');
  };

  const endSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sessions/${sessionId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (session?.id === sessionId) {
        setSession(null);
        stopStreaming();
      }
      toast.success('Sessão encerrada definitivamente');
    } catch (error) {
      toast.error('Erro ao encerrar sessão');
    }
  };

  useEffect(() => { syncLanguages(); }, []);

  // P0 isolation: active session guard — WS/poll events are validated against this ref
  const activeSessionIdRef = useRef(null);

  useEffect(() => {
    activeSessionIdRef.current = session?.id || null;
    // P0 isolation: never reuse transient UI data from a previous session
    setLiveTranscript([]);
    setSubtitleCount(0);
    setLatencyHistory([]);
    setAvgLatency(0);
    setLastText('');
    setParticipantCount(0);
    setUniqueCount(0);
    setParticipantEvents([]);
    setRtmpStreamQuality(null);
    setDetectedLang(null);
    setLiveLagMs(null);
    // Close any stale WS belonging to another session
    if (wsRef.current && wsRef.current._sessionId && wsRef.current._sessionId !== session?.id) {
      try { wsRef.current.close(); } catch (e) {}
      wsRef.current = null;
    }
    if (session) {
      setSourceLangMode(session.source_lang_mode || 'auto');
      setForcedSourceLang(session.forced_source_lang || null);
    }
  }, [session?.id]);

  const createSession = async () => {
    // Idiomas de tradução válidos = destinos diferentes da origem (exceto bilateral)
    const validTargets = targetLangs.filter((l) => bilateral || l !== sourceLang);
    if (validTargets.length === 0) {
      toast.error('Selecione pelo menos um idioma de tradução diferente do idioma de origem.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: sessionName.trim(),
        source_lang: sourceLang,
        target_langs: bilateral ? targetLangs : validTargets,
        glossary: glossary.filter(g => g.term.trim()),
        tts_voice: ttsVoice,
        translation_mode: bilateral ? 'openai' : translationMode,
        translation_audio_mode: translationAudioMode,
        azure_voice_prefs: azureVoicePrefs,
        bilateral,
        spoken_langs: bilateral ? spokenLangs : [],
      };
      if (scheduledFor) {
        payload.scheduled_for = new Date(scheduledFor).toISOString();
      }
      const response = await axios.post(`${API}/sessions`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(response.data);
      setMySessions((prev) => [response.data, ...prev]);
      setScheduledFor('');
      toast.success(scheduledFor ? 'Sessão agendada com sucesso!' : 'Sessão criada com sucesso!');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Por favor, faça login novamente');
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Conta pendente de aprovação. Contacte o administrador.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Erro ao criar sessão');
      } else {
        toast.error('Erro ao criar sessão');
      }
      console.error(error);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.info('Sessão terminada');
    navigate('/login');
  };

  const toggleTargetLang = (lang) => {
    setTargetLangs((prev) => {
      if (prev.includes(lang)) {
        // Don't allow removing if it's the last one
        if (prev.length === 1) {
          toast.warning('Pelo menos um idioma destino é necessário');
          return prev;
        }
        return prev.filter((l) => l !== lang);
      } else {
        return [...prev, lang];
      }
    });
  };

  const toggleSpokenLang = (lang) => {
    setSpokenLangs((prev) => {
      if (prev.includes(lang)) {
        if (prev.length <= 2) {
          toast.warning('Sessão bilateral requer pelo menos 2 idiomas falados');
          return prev;
        }
        return prev.filter((l) => l !== lang);
      }
      if (prev.length >= 4) {
        toast.warning('Máximo de 4 idiomas falados');
        return prev;
      }
      return [...prev, lang];
    });
  };

  const setSourceOverride = async (mode, lang = null) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/sessions/${session.id}/source-lang`, { mode, lang }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSourceLangMode(res.data.mode);
      setForcedSourceLang(res.data.forced_source_lang);
      toast.success(mode === 'auto' ? 'Deteção automática de idioma ativada' : `Idioma falado forçado: ${lang?.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar o idioma falado');
    }
  };

  const previewVoice = async (voice) => {
    if (previewPlaying && previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setPreviewPlaying(false);
      return;
    }
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/tts/preview`, { voice }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      previewAudioRef.current = audio;
      setPreviewPlaying(true);
      audio.onended = () => { setPreviewPlaying(false); previewAudioRef.current = null; };
      audio.play();
    } catch {
      toast.error('Erro ao gerar preview');
    } finally {
      setPreviewLoading(false);
    }
  };


  const previewAzureVoice = async (lang, voiceId) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setPreviewPlaying(false);
    }
    setAzurePreviewLang(lang);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/tts/preview`, { voice: voiceId, provider: 'azure' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      previewAudioRef.current = audio;
      audio.onended = () => { previewAudioRef.current = null; };
      audio.play();
    } catch {
      toast.error('Erro ao gerar preview Azure');
    } finally {
      setAzurePreviewLang(null);
    }
  };


  // WebSocket reconnect state
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const intentionalCloseRef = useRef(false);
  const isStreamingRef = useRef(false);

  const connectWebSocket = () => {
    if (!session) return null;
    const ws = new WebSocket(`${WS_URL}/api/ws/host/${session.id}`);
    ws._sessionId = session.id;
    
    ws.onopen = () => {
      // P0: Send auth first-message immediately on open
      const token = localStorage.getItem('token');
      if (token) {
        ws.send(JSON.stringify({ type: 'auth', token }));
      } else {
        console.error('No auth token available for WS');
        ws.close();
        return;
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        // P0 isolation: discard any event that is not for the active session
        if (ws._sessionId !== activeSessionIdRef.current) { try { ws.close(); } catch (e) {} return; }
        if (msg.session_id && msg.session_id !== activeSessionIdRef.current) return;
        if (msg.type === 'auth_ok') {
          setWsStatus('connected');
          if (reconnectAttemptsRef.current > 0) {
            toast.success('Reconectado');
            reconnectAttemptsRef.current = 0;
          }
          return;
        }
        if (msg.type === 'ping') return;
        if (msg.type === 'stats') {
          setSubtitleCount(c => c + 1);
          if (msg.detected_lang) setDetectedLang(msg.detected_lang);
          setLatencyHistory(prev => {
            const next = [...prev, msg.latency].slice(-20);
            const avg = next.reduce((a, b) => a + b, 0) / next.length;
            setAvgLatency(avg);
            return next;
          });
          setLastText(msg.text_preview || '');
          setParticipantCount(msg.participant_count || 0);
          if (msg.sustained_live_lag_ms !== undefined && msg.sustained_live_lag_ms !== null) setLiveLagMs(msg.sustained_live_lag_ms);
          if (msg.text_preview) {
            setLiveTranscript(prev => [...prev, {
              text: msg.text_preview,
              time: new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
              latency: msg.latency
            }]);
          }
        }
        if (msg.type === 'stats_update') {
          setParticipantCount(msg.participant_count || 0);
          if (msg.unique_count !== undefined) setUniqueCount(msg.unique_count);
        }
        if (msg.type === 'participant_event') {
          setParticipantCount(msg.participant_count || 0);
          if (msg.unique_count !== undefined) setUniqueCount(msg.unique_count);
          const langName = LANGUAGES.find(l => l.code === msg.language)?.name || msg.language;
          const eventText = msg.event === 'joined'
            ? `${langName} conectado`
            : `${langName} desconectado`;
          setParticipantEvents(prev => [...prev.slice(-9), {
            id: Date.now(),
            text: eventText,
            event: msg.event,
            time: new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
          }]);
        }
      } catch (e) {}
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setWsStatus('disconnected');
    };

    ws.onclose = (e) => {
      console.warn('WebSocket closed:', e.code, e.reason);
      setWsStatus('disconnected');
      
      // Auth failure — don't retry
      if (e.code === 4001) {
        toast.error(`Autenticação WS falhou: ${e.reason || 'token inválido'}`);
        return;
      }
      
      // Auto-reconnect if the close was NOT intentional and we're still streaming
      if (!intentionalCloseRef.current && isStreamingRef.current) {
        reconnectAttemptsRef.current++;
        if (reconnectAttemptsRef.current <= maxReconnectAttempts) {
          const delay = Math.min(1000 * reconnectAttemptsRef.current, 5000);
          toast.warning(`Conexão perdida. A reconectar (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
          setTimeout(() => {
            if (isStreamingRef.current && !intentionalCloseRef.current) {
              const newWs = connectWebSocket();
              if (newWs) wsRef.current = newWs;
            }
          }, delay);
        } else {
          toast.error('Não foi possível reconectar. A parar streaming.');
          stopStreaming();
        }
      }
    };

    return ws;
  };

  const startStreaming = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      
      mediaStreamRef.current = stream;
      intentionalCloseRef.current = false;
      reconnectAttemptsRef.current = 0;

      const ws = connectWebSocket();
      wsRef.current = ws;
      setWsStatus('connecting');

      // Wait for WS to open AND authenticate before setting up audio
      await new Promise((resolve, reject) => {
        const origOnMessage = ws.onmessage;
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'auth_ok') {
              ws.onmessage = origOnMessage;
              // Re-process auth_ok through normal handler
              origOnMessage(event);
              resolve();
              return;
            }
          } catch (e) {}
          if (origOnMessage) origOnMessage(event);
        };
        const origOnError = ws.onerror;
        ws.onerror = (e) => { origOnError(e); reject(e); };
        ws.onclose = (e) => {
          if (e.code === 4001) reject(new Error(`Auth failed: ${e.reason}`));
          else reject(new Error('WS closed'));
        };
        setTimeout(() => reject(new Error('WS auth timeout')), 10000);
      });

      // Start session
      try {
        await axios.post(`${API}/sessions/${session.id}/start`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (err) {
        console.error('Failed to start session:', err);
        toast.error('Erro ao iniciar sessão');
        ws.close();
        return;
      }
      setIsStreaming(true);
      isStreamingRef.current = true;
      setWsStatus('connected');
      toast.success('Streaming iniciado');

      // Setup audio processing
      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      const gainNode = audioContext.createGain();
      gainNode.gain.value = gainValue;
      gainNodeRef.current = gainNode;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      source.connect(gainNode);
      gainNode.connect(analyser);
      gainNode.connect(processor);
      processor.connect(audioContext.destination);

      let levelActive = true;
      const updateLevel = () => {
        if (!levelActive) return;
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(Math.min(100, (average / 255) * 100));
        requestAnimationFrame(updateLevel);
      };
      updateLevel();
      
      ws._stopLevel = () => { levelActive = false; };

      // VAD
      const VAD_THRESHOLD = 0.01;
      const VAD_HOLD_FRAMES = 2;
      const VAD_SILENCE_INTERVAL = 4;
      let silentFrameCount = 0;
      let vadCurrentlySpeaking = false;

      let bytesSent = 0;
      processor.onaudioprocess = (e) => {
        // Use wsRef.current to always send to the LATEST websocket (reconnected)
        const currentWs = wsRef.current;
        if (!currentWs || currentWs.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);

        let sumSquares = 0;
        for (let i = 0; i < inputData.length; i++) {
          sumSquares += inputData[i] * inputData[i];
        }
        const rms = Math.sqrt(sumSquares / inputData.length);

        if (rms >= VAD_THRESHOLD) {
          silentFrameCount = 0;
          if (!vadCurrentlySpeaking) {
            vadCurrentlySpeaking = true;
            setVadSpeaking(true);
          }
          const pcm16 = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            pcm16[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
          }
          currentWs.send(pcm16.buffer);
          bytesSent += pcm16.buffer.byteLength;
        } else {
          silentFrameCount++;
          if (silentFrameCount <= VAD_HOLD_FRAMES) {
            const pcm16 = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              pcm16[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
            }
            currentWs.send(pcm16.buffer);
            bytesSent += pcm16.buffer.byteLength;
          } else {
            if (vadCurrentlySpeaking) {
              vadCurrentlySpeaking = false;
              setVadSpeaking(false);
            }
            const framesSinceHold = silentFrameCount - VAD_HOLD_FRAMES;
            if (framesSinceHold === 1 || framesSinceHold % VAD_SILENCE_INTERVAL === 0) {
              currentWs.send(JSON.stringify({ type: 'vad_silence' }));
            }
          }
        }
      };
    } catch (error) {
      toast.error('Erro ao aceder ao microfone');
      console.error(error);
    }
  };

  const stopStreaming = async () => {
    try {
      intentionalCloseRef.current = true;
      isStreamingRef.current = false;
      
      if (wsRef.current) {
        if (wsRef.current._stopLevel) wsRef.current._stopLevel();
        wsRef.current.close();
        wsRef.current = null;
      }

      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }

      if (session) {
        await axios.post(`${API}/sessions/${session.id}/stop`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }

      setIsStreaming(false);
      setAudioLevel(0);
      setVadSpeaking(false);
      toast.info('Streaming parado');
    } catch (error) {
      console.error(error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const updateSpeaker = async (name) => {
    if (!session) return;
    try {
      await axios.put(`${API}/sessions/${session.id}/speaker`, { speaker: name }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCurrentSpeaker(name);
      toast.success(name ? `Orador: ${name}` : 'Orador removido');
    } catch (e) {
      toast.error('Erro ao atualizar orador');
    }
  };



  const handleGainChange = (val) => {
    const v = parseFloat(val);
    setGainValue(v);
    localStorage.setItem('host_audio_gain', v.toString());
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = v;
    }
  };

  const addGlossaryEntry = () => {
    const translations = {};
    targetLangs.forEach(lang => { translations[lang] = ''; });
    setGlossary(prev => [...prev, { term: '', translations }]);
  };

  const updateGlossaryTerm = (index, value) => {
    setGlossary(prev => prev.map((entry, i) => i === index ? { ...entry, term: value } : entry));
  };

  const updateGlossaryTranslation = (index, lang, value) => {
    setGlossary(prev => prev.map((entry, i) =>
      i === index ? { ...entry, translations: { ...entry.translations, [lang]: value } } : entry
    ));
  };

  const removeGlossaryEntry = (index) => {
    setGlossary(prev => prev.filter((_, i) => i !== index));
  };

  const saveGlossary = async () => {
    if (!session) return;
    try {
      const token = localStorage.getItem('token');
      const filtered = glossary.filter(g => g.term.trim());
      await axios.put(`${API}/sessions/${session.id}/glossary`, filtered, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Glossário guardado!');
    } catch (error) {
      toast.error('Erro ao guardar glossário');
    }
  };

  const toggleSessionSetting = async (key) => {
    if (!session) return;
    const newSettings = { ...sessionSettings, [key]: !sessionSettings[key] };
    setSessionSettings(newSettings);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sessions/${session.id}/settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Definição atualizada');
    } catch (error) {
      setSessionSettings(sessionSettings);
      toast.error('Erro ao atualizar definição');
    }
  };

  const openSessionInfoPopup = () => {
    if (!session) return;
    const url = `${window.location.origin}/session-info/${session.code}`;
    window.open(url, '_blank', 'width=800,height=900,menubar=no,toolbar=no');
  };

  const copyLiveTranscript = async () => {
    let text = '';
    if (liveTranscript.length > 0) {
      text = liveTranscript.map(e => `[${e.time}] ${e.text}`).join('\n');
    } else if (session) {
      try {
        const response = await axios.get(`${API}/sessions/${session.id}/transcription`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const entries = response.data?.entries || [];
        if (entries.length === 0) {
          toast.info('Sem transcrição disponível');
          return;
        }
        text = entries.map(e => `[${new Date(e.timestamp).toLocaleTimeString('pt-PT')}] ${e.original}`).join('\n');
      } catch {
        toast.error('Erro ao carregar transcrição');
        return;
      }
    }
    if (!text) { toast.info('Sem transcrição'); return; }
    const header = `Transcrição - Sessão ${session.code}\n${'─'.repeat(40)}\n\n`;
    navigator.clipboard.writeText(header + text);
    setTranscriptCopied(true);
    toast.success('Transcrição copiada!');
    setTimeout(() => setTranscriptCopied(false), 2000);
  };

  const downloadTranscript = async () => {
    let text = '';
    if (liveTranscript.length > 0) {
      text = liveTranscript.map(e => `[${e.time}] ${e.text}`).join('\n');
    } else if (session) {
      try {
        const response = await axios.get(`${API}/sessions/${session.id}/transcription`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const entries = response.data?.entries || [];
        if (entries.length === 0) {
          toast.info('Sem transcrição disponível');
          return;
        }
        text = entries.map(e => `[${new Date(e.timestamp).toLocaleTimeString('pt-PT')}] ${e.original}`).join('\n');
      } catch {
        toast.error('Erro ao carregar transcrição');
        return;
      }
    }
    if (!text) { toast.info('Sem transcrição'); return; }
    const header = `Transcrição - Sessão ${session.code}\nData: ${new Date().toLocaleString('pt-PT')}\n${'─'.repeat(40)}\n\n`;
    const blob = new Blob([header + text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcricao_${session.code}_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Ficheiro transferido!');
  };

  const downloadTranscriptPDF = async (lang = '') => {
    if (!session) return;
    const langParam = lang ? `?lang=${lang}` : '';
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${session.id}/transcription/pdf${langParam}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const langSuffix = lang ? `_${lang}` : '';
      a.download = `transcricao_${session.code}${langSuffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF transferido!');
    } catch {
      toast.error('Erro ao gerar PDF');
    }
  };

  // === RTMP Functions ===
  const startRtmpWorker = async () => {
    if (!session) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/sessions/${session.id}/rtmp/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRtmpUrl(response.data.rtmp_url || '');
      setRtmpStreamKey(response.data.stream_key || '');
      setRtmpWorkerRunning(true);
      setAudioSource('rtmp');
      toast.success('RTMP Worker iniciado! Configure o OBS com o URL exibido.');
    } catch (error) {
      toast.error('Erro ao iniciar RTMP Worker');
      console.error(error);
    }
  };

  const toggleCaptationPause = async () => {
    if (!session || captationBusy) return;
    setCaptationBusy(true);
    try {
      const token = localStorage.getItem('token');
      const action = captationPaused ? 'resume' : 'pause';
      await axios.post(`${API}/sessions/${session.id}/captation/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaptationPaused(!captationPaused);
      toast.success(captationPaused ? 'Tradução retomada' : 'Tradução em pausa — áudio a ser descartado (sem custo)');
    } catch (e) {
      toast.error('Erro ao alterar pausa de captação');
    } finally {
      setCaptationBusy(false);
    }
  };

  const stopRtmpWorker = async () => {
    if (!session) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sessions/${session.id}/rtmp/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRtmpWorkerRunning(false);
      setRtmpStreamActive(false);
      setRtmpStreamQuality(null);
      setAudioSource('mic');
      toast.info('RTMP Worker parado');
    } catch (error) {
      toast.error('Erro ao parar RTMP Worker');
    }
  };

  const checkRtmpStatus = async () => {
    if (!session) return;
    try {
      const response = await axios.get(`${API}/sessions/${session.id}/rtmp/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRtmpWorkerRunning(response.data.worker_running);
      setRtmpStreamActive(response.data.stream_active);
    } catch (error) {
      console.error('Error checking RTMP status:', error);
    }
  };

  const copyRtmpUrl = (text) => {
    navigator.clipboard.writeText(text);
    setRtmpCopied(true);
    toast.success('URL RTMP copiado!');
    setTimeout(() => setRtmpCopied(false), 2000);
  };

  // Participant count is now updated via WebSocket stats_update messages (no polling needed)

  // Poll RTMP status when worker is running
  useEffect(() => {
    if (!rtmpWorkerRunning || !session) return;
    const interval = setInterval(checkRtmpStatus, 5000);
    return () => clearInterval(interval);
  }, [rtmpWorkerRunning, session]);

  // RTMP mode: poll stats via HTTP instead of WebSocket (avoids host WS conflicts)
  useEffect(() => {
    if (!rtmpWorkerRunning || !session) return;
    let lastTranscriptCount = 0;
    let cancelled = false;
    const pollSessionId = session.id;

    const pollStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/sessions/${pollSessionId}/rtmp/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // P0 isolation: discard late responses after session switch/unmount
        if (cancelled || pollSessionId !== activeSessionIdRef.current) return;
        const data = response.data;
        setParticipantCount(data.participant_count || 0);
        
        // Update stream quality
        if (data.stream_quality) {
          setRtmpStreamQuality(data.stream_quality);
        }
        
        if (data.latency !== null && data.latency !== undefined) {
          setLatencyHistory(prev => {
            const next = [...prev, data.latency].slice(-20);
            const avg = next.reduce((a, b) => a + b, 0) / next.length;
            setAvgLatency(avg);
            return next;
          });
          setLastText(data.text_preview || '');
        }
        
        // Append new transcripts
        const transcripts = data.transcripts || [];
        if (transcripts.length > lastTranscriptCount) {
          const newOnes = transcripts.slice(lastTranscriptCount);
          setLiveTranscript(prev => [...prev, ...newOnes.map(t => ({
            text: t.text,
            time: t.time,
            latency: t.latency
          }))]);
          setSubtitleCount(c => c + newOnes.length);
          lastTranscriptCount = transcripts.length;
        }

        if (!data.worker_running) {
          setRtmpWorkerRunning(false);
        }
      } catch (e) {}
    };

    pollStats();
    const interval = setInterval(pollStats, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [rtmpWorkerRunning, session]);

  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, []);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [liveTranscript.length]);

  const navBtn = "h-8 px-2.5 text-xs text-slate-400 hover:text-white hover:bg-white/5";
  const panel = "rounded-lg border border-white/10 bg-[#0C1222]";
  const panelHead = "flex items-center gap-2 px-4 py-3 border-b border-white/10";
  const microLabel = "font-['Inter'] text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold";
  const inputCls = "w-full px-3 py-2 bg-[#0A0F1E] border border-white/10 rounded-md text-white placeholder-slate-600 focus:outline-none focus:border-[#00F0FF]/60 focus:ring-1 focus:ring-[#00F0FF]/30 font-['Inter'] text-sm transition-colors";

  const isLive = isStreaming || rtmpWorkerRunning;

  useEffect(() => {
    if (!session || !isLive) { setTtsRate(null); return; }
    let cancelled = false;
    const token = localStorage.getItem('token');
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/sessions/${session.id}/tts-rate`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setTtsRate(res.data);
      } catch (e) {}
    };
    poll();
    const iv = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [session, isLive]);

  const adjustTtsRate = async (payload) => {
    if (!session || ttsRateBusy) return;
    setTtsRateBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/sessions/${session.id}/tts-rate`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setTtsRate(res.data);
    } catch (e) {
      toast.error('Erro ao ajustar velocidade da voz');
    } finally {
      setTtsRateBusy(false);
    }
  };

  const statusBadge = (s) => {
    const map = {
      active: { label: 'Ativa', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/40' },
      stopped: { label: 'Encerrada', cls: 'bg-red-500/10 text-red-400 border-red-500/30' },
      archived: { label: 'Arquivada', cls: 'bg-white/5 text-slate-400 border-white/10' },
      paused: { label: 'Pausada', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/40' },
    };
    const m = map[s] || { label: 'Criada', cls: 'bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30' };
    return (
      <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full font-semibold border ${m.cls}`}>
        {s === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
        {m.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#050814] text-white">
      {!session ? (
        <>
          {/* ===== CONFIG VIEW ===== */}
          <header className="border-b border-white/10 bg-[#050814]/80 backdrop-blur-xl sticky top-0 z-40">
            <div className="max-w-[1500px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <img src="/logo-dih.png" alt="Do IT Happen" className="h-8" />
                <div className="h-6 w-px bg-white/10 hidden sm:block" />
                <h1 className="font-['Space_Grotesk'] text-lg font-semibold tracking-tight truncate" data-testid="host-console-title">
                  Host Console
                </h1>
              </div>
              <div className="flex items-center gap-0.5">
                {currentUser && (
                  <span className="font-['Inter'] text-xs text-slate-500 mr-2 hidden md:block">{currentUser.name}</span>
                )}
                {currentUser?.role === 'admin' && (
                  <Button variant="ghost" onClick={() => navigate('/admin')} className={navBtn} data-testid="admin-panel-btn">
                    <Shield className="w-3.5 h-3.5 mr-1" /> Admin
                  </Button>
                )}
                {currentUser?.role !== 'admin' && (
                  <Button variant="ghost" onClick={() => navigate('/portal')} className={navBtn} data-testid="portal-btn">
                    <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Portal
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate('/demo')} className={navBtn} data-testid="demo-mode-btn">
                  <FlaskConical className="w-3.5 h-3.5 mr-1" /> Demo
                </Button>
                <Button variant="ghost" onClick={() => navigate('/glossaries')} className={navBtn} data-testid="glossaries-btn">
                  <BookOpen className="w-3.5 h-3.5 mr-1" /> Glossários
                </Button>
                <Button variant="ghost" onClick={() => navigate('/history')} className={navBtn} data-testid="history-btn">
                  <History className="w-3.5 h-3.5 mr-1" /> Histórico
                </Button>
                <Button variant="ghost" onClick={() => navigate('/')} className={`${navBtn} hidden sm:inline-flex`} data-testid="back-to-home-btn">
                  <Home className="w-3.5 h-3.5 mr-1" /> Início
                </Button>
                <Button variant="ghost" onClick={handleLogout} className={navBtn} data-testid="logout-btn">
                  <LogOut className="w-3.5 h-3.5 mr-1" /> Sair
                </Button>
              </div>
            </div>
          </header>

          <main className="max-w-[1500px] mx-auto px-4 lg:px-8 py-8">
            <AccountWarningBanner warnings={accountWarnings} className="mb-6" />

            <div className="grid grid-cols-12 gap-6">
              {/* LEFT — Create Session (sticky) */}
              <section className="col-span-12 lg:col-span-5 xl:col-span-4">
                <div className={`${panel} lg:sticky lg:top-24`} data-testid="create-session-form">
                  <div className={panelHead}>
                    <Plus className="w-4 h-4 text-[#00F0FF]" />
                    <h2 className="font-['Space_Grotesk'] text-base font-semibold tracking-tight">Nova Sessão</h2>
                  </div>
                  <div className="p-5 space-y-5">
                    <div>
                      <label className={`${microLabel} mb-2 block`}>Nome do Evento</label>
                      <input
                        type="text"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Ex: Conferência Anual 2026"
                        className={inputCls}
                        data-testid="session-name-input"
                      />
                    </div>

                    <div>
                      <label className={`${microLabel} mb-2 block`}>Idioma de Origem</label>
                      <Select value={sourceLang} onValueChange={setSourceLang}>
                        <SelectTrigger className="bg-[#0A0F1E] border-white/10 text-white h-9" data-testid="source-lang-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#131B33] border-white/10 text-white">
                          {LANGUAGES.map((lang) => (
                            <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Bilateral */}
                    <div className="rounded-lg border border-white/10 bg-[#0A0F1E] p-3.5 space-y-3" data-testid="bilateral-section">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bilateral}
                          onChange={(e) => {
                            setBilateral(e.target.checked);
                            if (e.target.checked && !spokenLangs.includes(sourceLang)) {
                              setSpokenLangs([sourceLang, ...spokenLangs].slice(0, 4));
                            }
                          }}
                          className="w-4 h-4 accent-[#00F0FF]"
                          data-testid="bilateral-toggle"
                        />
                        <span className="font-['Inter'] text-sm text-white font-medium flex items-center gap-2">
                          <Languages className="w-3.5 h-3.5 text-[#00F0FF]" /> Sessão bilateral <span className="text-[10px] text-slate-500 font-normal">PoC</span>
                        </span>
                      </label>
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        O orador pode alternar entre idiomas. Deteção automática com opção de forçar durante o evento. Pipeline OpenAI apenas.
                      </p>
                      {bilateral && (
                        <div className="space-y-2">
                          <p className={microLabel}>Idiomas falados (2 a 4)</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {LANGUAGES.map((lang) => (
                              <button
                                key={lang.code}
                                type="button"
                                onClick={() => toggleSpokenLang(lang.code)}
                                className={`px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors ${
                                  spokenLangs.includes(lang.code)
                                    ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]'
                                    : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
                                }`}
                                data-testid={`spoken-lang-${lang.code}`}
                              >
                                {lang.name}
                              </button>
                            ))}
                          </div>
                          <p className="text-[10px] text-slate-600 leading-relaxed">
                            Os idiomas falados são adicionados aos destinos. O canal do idioma falado recebe legenda original sem voz (anti-eco).
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex items-baseline justify-between mb-2">
                        <label className={microLabel}>Idiomas de Destino</label>
                        <span className="font-['JetBrains_Mono'] text-[11px] text-[#00F0FF]">{targetLangs.length} selecionado(s)</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {LANGUAGES.filter((lang) => lang.code !== sourceLang).map((lang) => (
                          <button
                            key={lang.code}
                            type="button"
                            onClick={() => toggleTargetLang(lang.code)}
                            className={`px-2 py-2 rounded-md text-xs font-medium border transition-colors ${
                              targetLangs.includes(lang.code)
                                ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]'
                                : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`target-lang-${lang.code}`}
                          >
                            {lang.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className={`${microLabel} mb-2 flex items-center gap-1.5`}>
                        <Activity className="w-3.5 h-3.5" /> Motor de áudio (LAB)
                      </label>
                      <select value={translationAudioMode} onChange={(e) => setTranslationAudioMode(e.target.value)}
                        className={`${inputCls}`} data-testid="translation-audio-mode-select">
                        <option value="quality">Quality (atual — OpenAI TTS)</option>
                        <option value="azure_semantic_incremental">B3.4 · Azure Semantic Incremental (experimental)</option>
                      </select>
                      <p className="text-[10px] text-slate-600 mt-1">
                        {translationAudioMode === 'azure_semantic_incremental'
                          ? 'GPT streaming → unidades semânticas → Azure Raquel warm → áudio imediato + Live Lag monitor'
                          : 'Fluxo atual: tradução completa → TTS'}
                      </p>
                    </div>

                    <div>
                      <label className={`${microLabel} mb-2 flex items-center gap-1.5`}>
                        <CalendarDays className="w-3.5 h-3.5" /> Agendar (opcional)
                      </label>
                      <input
                        type="datetime-local"
                        value={scheduledFor}
                        onChange={(e) => setScheduledFor(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        max={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)}
                        className={`${inputCls} [color-scheme:dark]`}
                        data-testid="schedule-datetime-input"
                      />
                      <p className="text-[10px] text-slate-600 mt-1">
                        {scheduledFor ? `Agendada para: ${new Date(scheduledFor).toLocaleString('pt-PT')}` : 'Máximo 7 dias. Início sempre manual.'}
                      </p>
                    </div>

                    {!(translationMode === 'azure' || translationAudioMode === 'azure_semantic_incremental') && (
                    <div>
                      <label className={`${microLabel} mb-2 flex items-center gap-1.5`}>
                        <Volume2 className="w-3.5 h-3.5" /> Voz TTS
                      </label>
                      <div className="flex gap-2">
                        <Select value={ttsVoice} onValueChange={setTtsVoice}>
                          <SelectTrigger className="bg-[#0A0F1E] border-white/10 text-white flex-1 h-9" data-testid="tts-voice-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#131B33] border-white/10 text-white">
                            {TTS_VOICES.map((v) => (
                              <SelectItem key={v.id} value={v.id}>{v.name} — {v.desc}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-white/10 bg-white/5 hover:bg-white/10 text-white shrink-0 h-9 w-9"
                          onClick={() => previewVoice(ttsVoice)}
                          disabled={previewLoading}
                          data-testid="tts-voice-preview-btn"
                        >
                          {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : previewPlaying ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    )}

                    {(translationMode === 'azure' || translationAudioMode === 'azure_semantic_incremental') && (
                    <div data-testid="azure-voice-section">
                      <label className={`${microLabel} mb-2 flex items-center gap-1.5`}>
                        <Volume2 className="w-3.5 h-3.5" /> Vozes Azure por idioma
                      </label>
                      {azureVoices.length === 0 ? (
                        <p className="text-[10px] text-amber-400/80" data-testid="azure-voice-unavailable">
                          Vozes Azure indisponíveis — AZURE_SPEECH_KEY não configurada no servidor.
                        </p>
                      ) : (
                        <div className="space-y-1.5">
                          {azureVoices.filter((v) => targetLangs.includes(v.lang)).map((v) => {
                            const gender = azureVoicePrefs[v.lang] || 'female';
                            const selVoice = gender === 'male' ? v.male : v.female;
                            return (
                              <div key={v.lang} className="flex items-center gap-1.5 rounded-md bg-[#0A0F1E] border border-white/10 px-2 py-1.5" data-testid={`azure-voice-row-${v.lang}`}>
                                <span className="text-[10px] font-semibold text-slate-400 uppercase w-7 shrink-0">{v.lang}</span>
                                <button type="button"
                                  onClick={() => setAzureVoicePrefs((p) => ({ ...p, [v.lang]: 'female' }))}
                                  className={`flex-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${gender === 'female' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'}`}
                                  data-testid={`azure-voice-female-${v.lang}`}>
                                  ♀ {v.female.name}
                                </button>
                                <button type="button"
                                  onClick={() => setAzureVoicePrefs((p) => ({ ...p, [v.lang]: 'male' }))}
                                  className={`flex-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${gender === 'male' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'}`}
                                  data-testid={`azure-voice-male-${v.lang}`}>
                                  ♂ {v.male.name}
                                </button>
                                <Button variant="outline" size="icon"
                                  className="border-white/10 bg-white/5 hover:bg-white/10 text-white shrink-0 h-7 w-7"
                                  onClick={() => previewAzureVoice(v.lang, selVoice.id)}
                                  disabled={azurePreviewLang === v.lang}
                                  data-testid={`azure-voice-preview-${v.lang}`}>
                                  {azurePreviewLang === v.lang ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                                </Button>
                              </div>
                            );
                          })}
                          {targetLangs.filter((l) => !azureVoices.some((v) => v.lang === l)).length > 0 && (
                            <p className="text-[10px] text-slate-600" data-testid="azure-voice-no-profile-note">
                              Voz padrão do serviço (sem perfil F/M): {targetLangs.filter((l) => !azureVoices.some((v) => v.lang === l)).map((l) => l.toUpperCase()).join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    )}

                    {azureAvailable && (
                      <div data-testid="translation-mode-selector">
                        <label className={`${microLabel} mb-2 block`}>Motor de Tradução</label>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            onClick={() => setTranslationMode('openai')}
                            className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${translationMode === 'openai' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'}`}
                            data-testid="mode-openai-btn"
                          >
                            Cloud (OpenAI)
                          </button>
                          <button
                            onClick={() => setTranslationMode('azure')}
                            className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${translationMode === 'azure' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'}`}
                            data-testid="mode-azure-btn"
                          >
                            Azure AI
                          </button>
                        </div>
                      </div>
                    )}

                    {targetLangs.filter((l) => bilateral || l !== sourceLang).length === 0 && (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-400 text-xs font-medium flex items-center gap-2" data-testid="no-target-lang-warning">
                        <Languages className="w-4 h-4 shrink-0" /> Selecione pelo menos um idioma de tradução diferente da origem ({LANGUAGES.find((l) => l.code === sourceLang)?.name || sourceLang}).
                      </div>
                    )}

                    <Button
                      onClick={createSession}
                      disabled={targetLangs.filter((l) => bilateral || l !== sourceLang).length === 0}
                      className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-[#050814] font-bold py-6 text-base tracking-tight transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="create-session-submit-btn"
                    >
                      {scheduledFor ? 'Agendar Sessão' : 'Criar Sessão'}
                    </Button>

                    {/* Quick glossary terms (optional) */}
                    {targetLangs.length > 0 && (
                      <details className="rounded-lg border border-white/10 bg-[#0A0F1E]" data-testid="glossary-card">
                        <summary className="cursor-pointer select-none px-3.5 py-3 flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                          <BookOpen className="w-4 h-4 text-amber-400" />
                          Termos de glossário <span className="text-[10px] text-slate-600">(opcional{glossary.length > 0 ? ` · ${glossary.length}` : ''})</span>
                        </summary>
                        <div className="px-3.5 pb-3.5 space-y-3">
                          <p className="text-[11px] text-slate-500">Traduções fixas para nomes, marcas e siglas. Pode também associar um glossário completo depois de criar a sessão.</p>
                          {glossary.map((entry, idx) => (
                            <div key={idx} className="bg-white/5 rounded-md p-2.5 border border-white/10 space-y-2" data-testid={`glossary-entry-${idx}`}>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  placeholder="Termo original"
                                  value={entry.term}
                                  onChange={(e) => updateGlossaryTerm(idx, e.target.value)}
                                  className={`${inputCls} py-1.5`}
                                  data-testid={`glossary-term-${idx}`}
                                />
                                <button onClick={() => removeGlossaryEntry(idx)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md" data-testid={`glossary-remove-${idx}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                {targetLangs.map(lang => (
                                  <input
                                    key={lang}
                                    type="text"
                                    placeholder={LANGUAGES.find(l => l.code === lang)?.name}
                                    value={entry.translations[lang] || ''}
                                    onChange={(e) => updateGlossaryTranslation(idx, lang, e.target.value)}
                                    className={`${inputCls} py-1.5 text-xs`}
                                    data-testid={`glossary-translation-${idx}-${lang}`}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                          <button type="button" onClick={addGlossaryEntry} className="flex items-center gap-1.5 text-xs text-[#00F0FF] hover:text-[#33F3FF]" data-testid="glossary-add-btn">
                            <Plus className="w-3.5 h-3.5" /> Adicionar termo
                          </button>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </section>

              {/* RIGHT — Sessions list */}
              <section className="col-span-12 lg:col-span-7 xl:col-span-8">
                <div className={panel} data-testid="my-sessions-card">
                  <div className={panelHead}>
                    <RefreshCw className="w-4 h-4 text-[#00F0FF]" />
                    <h2 className="font-['Space_Grotesk'] text-base font-semibold tracking-tight">As Minhas Sessões</h2>
                    <span className="ml-auto font-['JetBrains_Mono'] text-xs text-slate-500">{mySessions.length}</span>
                  </div>
                  <div className="p-4 space-y-2.5">
                    {loadingSessions ? (
                      <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
                    ) : mySessions.length === 0 ? (
                      <div className="text-center py-14">
                        <Radio className="w-8 h-8 text-slate-700 mx-auto mb-3" />
                        <p className="font-['Inter'] text-sm text-slate-500">Ainda não tem sessões. Crie a primeira no painel ao lado.</p>
                      </div>
                    ) : (
                      <>
                        {(showAllSessions ? mySessions : mySessions.slice(0, 6)).map((s) => (
                          <div
                            key={s.id}
                            className="group flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-[#0A0F1E] hover:border-[#00F0FF]/30 transition-colors p-4"
                            data-testid={`my-session-${s.code}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                                <span className="font-['JetBrains_Mono'] text-base font-bold text-[#00F0FF] tracking-wider">{s.code}</span>
                                {s.name && <span className="font-['Inter'] text-sm text-white font-medium truncate max-w-[260px]">{s.name}</span>}
                                {statusBadge(s.status)}
                              </div>
                              <p className="font-['Inter'] text-xs text-slate-500 truncate">
                                {LANGUAGES.find(l => l.code === s.source_lang)?.name} → {s.target_langs.map(code => LANGUAGES.find(l => l.code === code)?.name).join(', ')}
                              </p>
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-600 font-['Inter']">
                                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {s.participant_count}</span>
                                {s.created_at && (
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(s.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}</span>
                                )}
                                {s.scheduled_for && (
                                  <span className="flex items-center gap-1 text-amber-400"><CalendarDays className="w-3 h-3" /> {new Date(s.scheduled_for).toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {s.status === 'stopped' || s.status === 'archived' ? (
                                <>
                                  <Button size="sm" variant="outline" className="h-8 border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white text-xs" onClick={() => navigate(`/report/${s.id}`)} data-testid={`report-session-${s.code}`}>
                                    <FileText className="w-3.5 h-3.5 mr-1" /> Relatório
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-8 bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 text-xs"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        await axios.post(`${API}/sessions/${s.id}/reopen`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                        toast.success('Sessão reaberta!');
                                        const updated = { ...s, status: 'created' };
                                        setMySessions(prev => prev.map(x => x.id === s.id ? updated : x));
                                      } catch (err) {
                                        toast.error('Erro ao reabrir sessão');
                                      }
                                    }}
                                    data-testid={`reopen-session-${s.code}`}
                                  >
                                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reabrir
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" className="h-8 bg-[#00F0FF] hover:bg-[#33F3FF] text-[#050814] font-semibold text-xs" onClick={() => resumeSession(s)} data-testid={`resume-session-${s.code}`}>
                                  <Play className="w-3.5 h-3.5 mr-1" /> Abrir
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-600 hover:text-red-400 hover:bg-red-500/10" onClick={() => endSession(s.id)} data-testid={`end-session-${s.code}`}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {mySessions.length > 6 && (
                          <Button variant="ghost" className="w-full text-slate-500 hover:text-white text-xs h-8" onClick={() => setShowAllSessions(!showAllSessions)} data-testid="toggle-all-sessions-btn">
                            {showAllSessions ? 'Mostrar menos' : `Ver todas as sessões (${mySessions.length - 6} mais)`}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </main>
        </>
      ) : (
        <>
          {/* ===== LIVE CONSOLE ===== */}
          <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050814]/85 backdrop-blur-xl" data-testid="session-info-card">
            <div className="max-w-[1600px] mx-auto px-4 lg:px-6 h-16 flex items-center gap-3">
              <button
                onClick={() => {
                  if (isStreaming) { toast.warning('Pare a captação antes de sair da sessão'); return; }
                  setSession(null);
                  fetchMySessions();
                }}
                className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors shrink-0"
                data-testid="back-to-sessions-btn"
                title="Voltar às sessões"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="font-['Space_Grotesk'] text-base font-semibold tracking-tight truncate max-w-[220px]">{session.name || 'Sessão'}</h1>
                  <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full font-bold border ${isLive ? 'bg-red-500/10 text-red-400 border-red-500/40' : 'bg-white/5 text-slate-400 border-white/10'}`} data-testid="transmission-status">
                    <span className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-red-500 animate-pulse' : 'bg-slate-500'}`} />
                    {isLive ? 'EM DIRETO' : 'PRONTA'}
                  </span>
                </div>
                <p className="font-['Inter'] text-[11px] text-slate-500 truncate hidden sm:block" data-testid="source-lang-display">
                  {session.bilateral
                    ? `Bilateral (${(session.spoken_langs || []).map(c => c.toUpperCase()).join(' ⇄ ')})`
                    : LANGUAGES.find(l => l.code === session.source_lang)?.name} → {(session.target_langs || []).map(c => c.toUpperCase()).join(' · ')}
                </p>
              </div>

              <div className="flex-1" />

              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#0C1222] px-3 py-1.5">
                <span className="font-['JetBrains_Mono'] text-xl font-bold text-[#00F0FF] tracking-[0.2em]" data-testid="session-code">{session.code}</span>
                <button onClick={() => copyToClipboard(session.code)} className="p-1 text-slate-500 hover:text-white transition-colors" data-testid="copy-code-btn" title="Copiar código">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <div className="w-px h-5 bg-white/10" />
                <button onClick={() => setQrOpen(true)} className="p-1 text-slate-400 hover:text-[#00F0FF] transition-colors" data-testid="qr-code-open-btn" title="Mostrar QR code">
                  <QrCode className="w-4 h-4" />
                </button>
                <button onClick={() => copyToClipboard(session.join_url)} className="p-1 text-slate-500 hover:text-white transition-colors" data-testid="copy-url-btn" title="Copiar link de acesso">
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="hidden md:flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#0C1222] px-3 py-2" title="Participantes ligados">
                <Users className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-['JetBrains_Mono'] text-base font-bold" data-testid="participant-count">{participantCount}</span>
                {uniqueCount > 0 && <span className="font-['Inter'] text-[10px] text-slate-500" data-testid="unique-count">({uniqueCount} únicos)</span>}
              </div>

              <div className="hidden lg:flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={openSessionInfoPopup} className="h-8 px-2 text-xs text-slate-400 hover:text-white hover:bg-white/5" data-testid="open-session-info-popup-btn" title="Abrir info para AV (pop-up)">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Info AV
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAvOverlayOpen(true)} className="h-8 px-2 text-xs text-purple-300 hover:text-purple-200 hover:bg-purple-500/10" data-testid="av-overlay-toggle" title="Links de overlay para OBS/vMix">
                  <Monitor className="w-3.5 h-3.5 mr-1" /> Overlay
                </Button>
                <Button size="sm" variant="ghost" onClick={() => window.open(`/event/${session.id}`, '_blank')} className="h-8 px-2 text-xs text-[#00F0FF] hover:text-[#33F3FF] hover:bg-[#00F0FF]/10" data-testid="open-event-mode-btn" title="Cockpit técnico em tempo real">
                  <Gauge className="w-3.5 h-3.5 mr-1" /> Event Mode
                </Button>
              </div>
            </div>
          </div>

          <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-5">
            <AccountWarningBanner warnings={accountWarnings} className="mb-4" />

            <div className="grid grid-cols-12 gap-4">
              {/* SIDEBAR — command center */}
              <aside className="col-span-12 lg:col-span-4 xl:col-span-3 space-y-4">
                {/* 1. AUDIO CAPTURE — critical */}
                <div className={`${panel} border-[#00F0FF]/20`} data-testid="audio-control-card">
                  <div className={panelHead}>
                    <Activity className="w-4 h-4 text-[#00F0FF]" />
                    <h3 className="font-['Space_Grotesk'] text-sm font-semibold tracking-tight">Fonte de Áudio</h3>
                    {isStreaming && <span className="ml-auto w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        onClick={() => {
                          if (audioSource === 'rtmp' && rtmpWorkerRunning) stopRtmpWorker();
                          setAudioSource('mic');
                        }}
                        disabled={isStreaming}
                        className={`py-2 px-2 rounded-md text-xs font-semibold border transition-colors flex items-center justify-center gap-1.5 ${
                          audioSource === 'mic' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                        } ${isStreaming ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid="audio-source-mic-btn"
                      >
                        <Mic className="w-3.5 h-3.5" /> Microfone
                      </button>
                      <button
                        onClick={() => { if (!isStreaming) setAudioSource('rtmp'); }}
                        disabled={isStreaming}
                        className={`py-2 px-2 rounded-md text-xs font-semibold border transition-colors flex items-center justify-center gap-1.5 ${
                          audioSource === 'rtmp' ? 'bg-purple-500/15 border-purple-500/50 text-purple-300' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                        } ${isStreaming ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid="audio-source-rtmp-btn"
                      >
                        <Radio className="w-3.5 h-3.5" /> RTMP
                      </button>
                    </div>

                    {audioSource === 'mic' ? (
                      <>
                        <Button
                          onClick={isStreaming ? stopStreaming : startStreaming}
                          className={`w-full py-7 text-base font-bold tracking-tight transition-colors ${
                            isStreaming
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-[#00F0FF] hover:bg-[#33F3FF] text-[#050814]'
                          }`}
                          data-testid="toggle-streaming-btn"
                        >
                          {isStreaming ? (<><MicOff className="w-5 h-5 mr-2" /> Parar Captação</>) : (<><Mic className="w-5 h-5 mr-2" /> Iniciar Captação</>)}
                        </Button>

                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className={microLabel}>Nível de Áudio</label>
                            {isStreaming && (
                              <span className="flex items-center gap-1.5" data-testid="vad-status">
                                <span className={`w-1.5 h-1.5 rounded-full ${vadSpeaking ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`} />
                                <span className="font-['Inter'] text-[10px] text-slate-500">{vadSpeaking ? 'Voz detetada' : 'Silêncio (VAD)'}</span>
                              </span>
                            )}
                          </div>
                          <div className="flex items-end gap-[2px] h-7 rounded-md bg-[#0A0F1E] border border-white/10 px-1.5 py-1" data-testid="audio-level-meter">
                            {Array.from({ length: 24 }).map((_, i) => {
                              const lit = audioLevel >= ((i + 1) / 24) * 100;
                              const color = i < 14 ? 'bg-emerald-400' : i < 20 ? 'bg-yellow-400' : 'bg-red-500';
                              return (
                                <div key={i} className={`flex-1 rounded-[1px] transition-colors ${lit ? color : 'bg-white/10'}`} style={{ height: `${30 + (i / 23) * 70}%` }} />
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label className={`${microLabel} mb-1.5 flex items-center gap-1.5`}>
                            <Volume2 className="w-3 h-3" /> Ganho <span className="font-['JetBrains_Mono'] text-[#00F0FF] normal-case tracking-normal">{gainValue.toFixed(1)}x</span>
                          </label>
                          <input
                            type="range" min="0.5" max="5.0" step="0.5"
                            value={gainValue}
                            onChange={(e) => handleGainChange(e.target.value)}
                            className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00F0FF]"
                            data-testid="gain-slider"
                          />
                          <div className="flex justify-between text-[9px] text-slate-700 mt-1 font-['JetBrains_Mono']"><span>0.5x</span><span>2.5x</span><span>5x</span></div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-3">
                        {!rtmpWorkerRunning ? (
                          <Button onClick={startRtmpWorker} className="w-full py-7 text-base font-bold bg-purple-600 hover:bg-purple-700" data-testid="start-rtmp-btn">
                            <Radio className="w-5 h-5 mr-2" /> Iniciar RTMP
                          </Button>
                        ) : (
                          <>
                            <div className="bg-[#0A0F1E] rounded-lg p-3 border border-purple-500/30 space-y-2">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full ${rtmpStreamActive ? 'bg-emerald-400 animate-pulse' : 'bg-yellow-400 animate-pulse'}`} />
                                <span className="font-['Inter'] text-xs font-medium text-slate-300">{rtmpStreamActive ? 'Stream ativo' : 'A aguardar stream do OBS…'}</span>
                              </div>
                              <div>
                                <label className="font-['Inter'] text-[9px] text-slate-600 uppercase tracking-wider">URL do Servidor</label>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <code className="font-['JetBrains_Mono'] text-[11px] text-purple-300 bg-black/40 px-2 py-1 rounded flex-1 truncate" data-testid="rtmp-server-url">
                                    {rtmpUrl ? rtmpUrl.replace(`/${rtmpStreamKey}`, '/') : ''}
                                  </code>
                                  <button onClick={() => copyRtmpUrl(rtmpUrl ? rtmpUrl.replace(`/${rtmpStreamKey}`, '/') : '')} className="p-1 text-slate-500 hover:text-white" data-testid="copy-rtmp-server-btn">
                                    {rtmpCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <label className="font-['Inter'] text-[9px] text-slate-600 uppercase tracking-wider">Stream Key</label>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <code className="font-['JetBrains_Mono'] text-[11px] text-purple-300 bg-black/40 px-2 py-1 rounded flex-1" data-testid="rtmp-stream-key">{rtmpStreamKey}</code>
                                  <button onClick={() => copyRtmpUrl(rtmpStreamKey)} className="p-1 text-slate-500 hover:text-white" data-testid="copy-rtmp-key-btn">
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            {rtmpStreamActive && rtmpStreamQuality && rtmpStreamQuality.chunks_received > 0 && (
                              <div className="bg-[#0A0F1E] rounded-lg p-3 border border-white/10 space-y-2.5" data-testid="rtmp-quality-panel">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5">
                                    <Signal className="w-3.5 h-3.5 text-slate-500" />
                                    <span className="font-['Inter'] text-[9px] text-slate-500 uppercase tracking-wider">Qualidade do Stream</span>
                                  </div>
                                  <span className={`font-['JetBrains_Mono'] text-xs font-bold ${
                                    rtmpStreamQuality.signal_quality >= 60 ? 'text-emerald-400' : rtmpStreamQuality.signal_quality >= 30 ? 'text-amber-400' : 'text-red-400'
                                  }`} data-testid="rtmp-signal-quality">{rtmpStreamQuality.signal_quality}%</span>
                                </div>
                                <div className="flex items-end gap-[3px] h-5" data-testid="rtmp-signal-bars">
                                  {[20, 40, 60, 80, 100].map((threshold, i) => (
                                    <div key={i} className={`w-[6px] rounded-sm transition-colors ${
                                      rtmpStreamQuality.signal_quality >= threshold
                                        ? rtmpStreamQuality.signal_quality >= 60 ? 'bg-emerald-400' : rtmpStreamQuality.signal_quality >= 30 ? 'bg-amber-400' : 'bg-red-400'
                                        : 'bg-white/10'
                                    }`} style={{ height: `${6 + i * 3.5}px` }} />
                                  ))}
                                  <div className="ml-2 flex-1">
                                    <div className="flex items-center gap-1">
                                      <Volume2 className="w-3 h-3 text-slate-600" />
                                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${
                                          rtmpStreamQuality.audio_level > 70 ? 'bg-red-400' : rtmpStreamQuality.audio_level > 40 ? 'bg-emerald-400' : rtmpStreamQuality.audio_level > 10 ? 'bg-amber-400' : 'bg-slate-600'
                                        }`} style={{ width: `${Math.min(100, rtmpStreamQuality.audio_level)}%` }} data-testid="rtmp-audio-level" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-1 border-t border-white/10">
                                  <div className="text-center">
                                    <div className="font-['JetBrains_Mono'] text-xs text-emerald-400" data-testid="rtmp-chunks-processed">{rtmpStreamQuality.chunks_processed}</div>
                                    <div className="font-['Inter'] text-[9px] text-slate-600">processados</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-['JetBrains_Mono'] text-xs text-slate-400" data-testid="rtmp-chunks-silent">{rtmpStreamQuality.chunks_silent}</div>
                                    <div className="font-['Inter'] text-[9px] text-slate-600">silêncio</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-['JetBrains_Mono'] text-xs text-blue-400" data-testid="rtmp-chunks-rate">{rtmpStreamQuality.chunks_per_minute}/min</div>
                                    <div className="font-['Inter'] text-[9px] text-slate-600">taxa</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {captationPaused && (
                              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-400 text-xs font-semibold flex items-center gap-2" data-testid="captation-paused-banner">
                                <Pause className="w-4 h-4 shrink-0" /> Tradução em pausa — o áudio está a ser descartado (sem custo STT/TTS)
                              </div>
                            )}
                            <Button
                              onClick={toggleCaptationPause}
                              disabled={captationBusy}
                              className={`w-full font-semibold ${captationPaused ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                              data-testid="captation-toggle-btn"
                            >
                              {captationBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : captationPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                              {captationPaused ? 'Retomar Tradução' : 'Pausar Tradução (vídeo/intervalo)'}
                            </Button>
                            <Button onClick={stopRtmpWorker} className="w-full bg-red-600 hover:bg-red-700" data-testid="stop-rtmp-btn">
                              <XCircle className="w-4 h-4 mr-2" /> Parar RTMP
                            </Button>
                          </>
                        )}
                        <p className="font-['Inter'] text-[10px] text-slate-600 text-center">No OBS: Definições → Stream → Serviço: Personalizado</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. TELEMETRY */}
                <div className={panel} data-testid="stats-card">
                  <div className={panelHead}>
                    <BarChart3 className="w-4 h-4 text-slate-400" />
                    <h3 className="font-['Space_Grotesk'] text-sm font-semibold tracking-tight">Telemetria</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-md bg-[#0A0F1E] border border-white/10 p-2.5" data-testid="latency-card">
                        <div className={microLabel}>Latência</div>
                        <div className={`font-['JetBrains_Mono'] text-xl font-bold mt-0.5 ${
                          avgLatency === 0 ? 'text-slate-600' : avgLatency < 3 ? 'text-emerald-400' : avgLatency < 6 ? 'text-yellow-400' : 'text-red-400'
                        }`} data-testid="avg-latency">
                          {avgLatency > 0 ? `${avgLatency.toFixed(1)}s` : '—'}
                        </div>
                        {liveLagMs !== null && (
                          <div className={`text-[10px] font-['JetBrains_Mono'] mt-0.5 ${liveLagMs < 3000 ? 'text-emerald-400' : liveLagMs < 6000 ? 'text-yellow-400' : 'text-red-400'}`} data-testid="live-lag-badge">
                            live lag {(liveLagMs / 1000).toFixed(1)}s
                          </div>
                        )}
                      </div>
                      <div className="rounded-md bg-[#0A0F1E] border border-white/10 p-2.5">
                        <div className={microLabel}>Legendas</div>
                        <div className="font-['JetBrains_Mono'] text-xl font-bold text-white mt-0.5" data-testid="subtitle-count">{subtitleCount}</div>
                      </div>
                    </div>
                    {latencyHistory.length > 0 && (
                      <div className="flex items-end gap-0.5 h-7">
                        {latencyHistory.map((l, i) => (
                          <div key={i} className={`flex-1 rounded-sm ${l < 3 ? 'bg-emerald-500/70' : l < 6 ? 'bg-yellow-500/70' : 'bg-red-500/70'}`} style={{ height: `${Math.min(100, (l / 10) * 100)}%` }} />
                        ))}
                      </div>
                    )}
                    {ttsRate && (
                      <div className="rounded-md bg-[#0A0F1E] border border-white/10 p-2.5" data-testid="tts-rate-control">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className={microLabel}>Velocidade da voz</div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-['JetBrains_Mono'] text-lg font-bold text-[#00F0FF]" data-testid="tts-rate-value">{ttsRate.effective_rate.toFixed(2)}×</span>
                            {ttsRate.adaptive > 0 && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" data-testid="tts-rate-auto-badge">AUTO +{Math.round(ttsRate.adaptive * 100)}%</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button variant="outline" size="icon" className="h-7 w-7 border-white/10 bg-white/5 hover:bg-white/10 text-white shrink-0"
                            onClick={() => adjustTtsRate({ delta: -ttsRate.bias_step })}
                            disabled={ttsRateBusy || ttsRate.manual_bias <= ttsRate.bias_min}
                            data-testid="tts-rate-minus-btn">
                            <span className="text-base leading-none">−</span>
                          </Button>
                          <div className="flex-1 text-center">
                            <span className="text-[10px] font-['JetBrains_Mono'] text-slate-400" data-testid="tts-rate-manual">
                              manual {ttsRate.manual_bias >= 0 ? '+' : ''}{Math.round(ttsRate.manual_bias * 100)}%
                            </span>
                          </div>
                          <Button variant="outline" size="icon" className="h-7 w-7 border-white/10 bg-white/5 hover:bg-white/10 text-white shrink-0"
                            onClick={() => adjustTtsRate({ delta: ttsRate.bias_step })}
                            disabled={ttsRateBusy || ttsRate.manual_bias >= ttsRate.bias_max}
                            data-testid="tts-rate-plus-btn">
                            <span className="text-base leading-none">+</span>
                          </Button>
                          {ttsRate.manual_bias !== 0 && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] text-slate-400 hover:text-white shrink-0"
                              onClick={() => adjustTtsRate({ reset: true })}
                              disabled={ttsRateBusy}
                              data-testid="tts-rate-reset-btn">
                              repor
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="font-['Inter'] text-xs text-slate-500">Captação</span>
                        <span className={`font-['Inter'] text-xs font-semibold ${isLive ? 'text-emerald-400' : 'text-slate-400'}`} data-testid="streaming-status">
                          {isStreaming ? 'Ativa (Mic)' : rtmpWorkerRunning ? 'Ativa (RTMP)' : 'Pronta a iniciar'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-['Inter'] text-xs text-slate-500">{audioSource === 'rtmp' ? 'Ligação RTMP' : 'Ligação WS'}</span>
                        <span className={`font-['Inter'] text-xs font-semibold ${
                          audioSource === 'rtmp'
                            ? (rtmpWorkerRunning ? 'text-emerald-400' : 'text-slate-400')
                            : (wsStatus === 'connected' ? 'text-emerald-400' : wsStatus === 'connecting' ? 'text-amber-400' : isStreaming ? 'text-red-400' : 'text-slate-400')
                        }`} data-testid="ws-status">
                          {audioSource === 'rtmp'
                            ? (rtmpWorkerRunning ? 'Ativa' : 'Em espera')
                            : (wsStatus === 'connected' ? 'Ligada' : wsStatus === 'connecting' ? 'A ligar…' : isStreaming ? 'Desligada' : 'Em espera')}
                        </span>
                      </div>
                    </div>
                    {lastText && (
                      <div className="pt-2 border-t border-white/10">
                        <span className={microLabel}>Último segmento</span>
                        <p className="font-['Inter'] text-xs text-slate-300 mt-1 line-clamp-2" data-testid="last-transcribed-text">{lastText}…</p>
                      </div>
                    )}
                    {participantEvents.length > 0 && (
                      <div className="pt-2 border-t border-white/10 space-y-1 max-h-20 overflow-y-auto">
                        {participantEvents.slice(-4).map((evt) => (
                          <div key={evt.id} className="flex items-center gap-2 text-[11px]">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${evt.event === 'joined' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                            <span className="text-slate-600 font-['JetBrains_Mono']">{evt.time}</span>
                            <span className={evt.event === 'joined' ? 'text-emerald-400' : 'text-red-400'}>{evt.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(isStreaming || session?.audio_source === 'rtmp') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full h-8 border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white text-xs"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${API}/sessions/${session.id}/clear-subtitles`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Legendas limpas');
                          } catch (err) {
                            toast.error('Erro ao limpar legendas');
                          }
                        }}
                        data-testid="clear-subtitles-btn"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1.5" /> Limpar Legendas
                      </Button>
                    )}
                  </div>
                </div>

                {/* 3. BILATERAL OVERRIDE */}
                {session.bilateral && (
                  <div className={`${panel} border-cyan-500/20`} data-testid="bilateral-control-card">
                    <div className={panelHead}>
                      <Languages className="w-4 h-4 text-[#00F0FF]" />
                      <h3 className="font-['Space_Grotesk'] text-sm font-semibold tracking-tight">Idioma Falado</h3>
                      {detectedLang && sourceLangMode === 'auto' && (
                        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-[#00F0FF]" data-testid="detected-lang-badge">
                          {LANGUAGES.find(l => l.code === detectedLang)?.name || detectedLang.toUpperCase()}
                        </span>
                      )}
                      {sourceLangMode === 'manual' && forcedSourceLang && (
                        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300" data-testid="forced-lang-badge">
                          Forçado: {LANGUAGES.find(l => l.code === forcedSourceLang)?.name || forcedSourceLang.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="p-4 space-y-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => setSourceOverride('auto')}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                            sourceLangMode === 'auto' ? 'bg-[#00F0FF]/15 border-[#00F0FF]/50 text-[#00F0FF]' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                          }`}
                          data-testid="source-mode-auto-btn"
                        >
                          AUTO
                        </button>
                        {(session.spoken_langs || []).map((lang) => (
                          <button
                            key={lang}
                            onClick={() => setSourceOverride('manual', lang)}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                              sourceLangMode === 'manual' && forcedSourceLang === lang
                                ? 'bg-amber-500/15 border-amber-500/50 text-amber-300'
                                : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                            }`}
                            data-testid={`source-force-${lang}-btn`}
                          >
                            {LANGUAGES.find(l => l.code === lang)?.name || lang.toUpperCase()}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-600 leading-relaxed">Em AUTO o idioma de cada frase é detetado automaticamente. O canal do idioma falado recebe a legenda original sem voz (anti-eco).</p>
                    </div>
                  </div>
                )}

                {/* 4. SESSION CONTROLS (participants + voice + speaker) */}
                <div className={panel} data-testid="participant-controls-card">
                  <button onClick={() => setParticipantControlsOpen(!participantControlsOpen)} className="w-full px-4 py-3 flex items-center gap-2 hover:bg-white/[0.03] transition-colors">
                    <Monitor className="w-4 h-4 text-purple-400" />
                    <h3 className="font-['Space_Grotesk'] text-sm font-semibold tracking-tight">Controlos da Sessão</h3>
                    {participantControlsOpen ? <ChevronDown className="w-4 h-4 text-slate-500 ml-auto" /> : <ChevronRight className="w-4 h-4 text-slate-500 ml-auto" />}
                  </button>
                  {participantControlsOpen && (
                    <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
                      <div className="flex items-center justify-between rounded-md bg-[#0A0F1E] border border-white/10 p-3">
                        <div>
                          <p className="font-['Inter'] text-xs font-medium text-white">Modo Teleponto</p>
                          <p className="font-['Inter'] text-[10px] text-slate-500">Ecrã inteiro para participantes.</p>
                        </div>
                        <button
                          onClick={() => toggleSessionSetting('teleprompter_enabled')}
                          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${sessionSettings.teleprompter_enabled ? 'bg-[#00F0FF]' : 'bg-white/15'}`}
                          data-testid="toggle-teleprompter-setting"
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${sessionSettings.teleprompter_enabled ? 'translate-x-5.5 translate-x-[22px]' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between rounded-md bg-[#0A0F1E] border border-white/10 p-3">
                        <div>
                          <p className="font-['Inter'] text-xs font-medium text-white">Modo Intérprete</p>
                          <p className="font-['Inter'] text-[10px] text-slate-500">Dois idiomas lado a lado.</p>
                        </div>
                        <button
                          onClick={() => toggleSessionSetting('interpreter_enabled')}
                          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${sessionSettings.interpreter_enabled ? 'bg-[#00F0FF]' : 'bg-white/15'}`}
                          data-testid="toggle-interpreter-setting"
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${sessionSettings.interpreter_enabled ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      <div className="rounded-md bg-[#0A0F1E] border border-white/10 p-3">
                        <p className="font-['Inter'] text-xs font-medium text-white mb-2">Orador Atual</p>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={speakerInput}
                            onChange={(e) => setSpeakerInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') updateSpeaker(speakerInput); }}
                            placeholder="Nome do orador…"
                            className={`${inputCls} py-1.5 text-xs`}
                            data-testid="speaker-name-input"
                          />
                          <Button
                            size="sm"
                            onClick={() => updateSpeaker(speakerInput)}
                            disabled={speakerInput === currentSpeaker}
                            className="h-8 bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/25 disabled:opacity-30 text-xs"
                            data-testid="speaker-name-update-btn"
                          >
                            {currentSpeaker ? 'Alterar' : 'Definir'}
                          </Button>
                        </div>
                        {currentSpeaker && (
                          <p className="text-[10px] text-[#00F0FF] mt-1.5 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" /> A falar: {currentSpeaker}
                          </p>
                        )}
                      </div>
                      <div className="rounded-md bg-[#0A0F1E] border border-white/10 p-3" data-testid="session-tts-voice-card">
                        <p className="font-['Inter'] text-xs font-medium text-white mb-2 flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5 text-[#00F0FF]" /> Voz TTS</p>
                        <div className="flex gap-1.5">
                          <Select value={ttsVoice} onValueChange={async (v) => {
                            setTtsVoice(v);
                            try {
                              const token = localStorage.getItem('token');
                              await axios.put(`${API}/sessions/${session.id}/tts-voice`, { tts_voice: v }, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success(`Voz alterada para ${v}`);
                            } catch { toast.error('Erro ao alterar voz'); }
                          }}>
                            <SelectTrigger className="bg-[#050814] border-white/10 text-white flex-1 h-8 text-xs" data-testid="session-tts-voice-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#131B33] border-white/10 text-white">
                              {TTS_VOICES.map((v) => (
                                <SelectItem key={v.id} value={v.id}>{v.name} — {v.desc}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline" size="icon"
                            className="border-white/10 bg-white/5 hover:bg-white/10 text-white shrink-0 h-8 w-8"
                            onClick={() => previewVoice(ttsVoice)}
                            disabled={previewLoading}
                            data-testid="session-tts-voice-preview-btn"
                          >
                            {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : previewPlaying ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 5. UNIFIED GLOSSARY */}
                <div className={panel} data-testid="session-glossary-card">
                  <div className={panelHead}>
                    <BookOpen className="w-4 h-4 text-amber-400" />
                    <h3 className="font-['Space_Grotesk'] text-sm font-semibold tracking-tight">Glossário</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <GlossaryLinkCard sessionId={session.id} />
                    <div className="pt-1">
                      <p className={`${microLabel} mb-2`}>Termos rápidos desta sessão</p>
                      <div className="space-y-2">
                        {glossary.map((entry, idx) => (
                          <div key={idx} className="rounded-md bg-[#0A0F1E] border border-white/10 p-2 space-y-1.5" data-testid={`session-glossary-entry-${idx}`}>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="text"
                                placeholder="Termo"
                                value={entry.term}
                                onChange={(e) => updateGlossaryTerm(idx, e.target.value)}
                                className={`${inputCls} py-1 text-xs`}
                              />
                              <button onClick={() => removeGlossaryEntry(idx)} className="p-1 text-red-400/70 hover:text-red-300">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                              {(session.target_langs || []).map(lang => (
                                <input
                                  key={lang}
                                  type="text"
                                  placeholder={LANGUAGES.find(l => l.code === lang)?.name}
                                  value={entry.translations?.[lang] || ''}
                                  onChange={(e) => updateGlossaryTranslation(idx, lang, e.target.value)}
                                  className={`${inputCls} py-1 text-xs`}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <button onClick={addGlossaryEntry} className="flex items-center gap-1 text-xs text-[#00F0FF] hover:text-[#33F3FF]" data-testid="session-glossary-add-btn">
                          <Plus className="w-3.5 h-3.5" /> Adicionar
                        </button>
                        {glossary.length > 0 && (
                          <Button size="sm" onClick={saveGlossary} className="ml-auto h-7 bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 text-[11px]" data-testid="session-glossary-save-btn">
                            Guardar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </aside>

              {/* MAIN — transcription feed */}
              <section className="col-span-12 lg:col-span-8 xl:col-span-9">
                <div className={`${panel} flex flex-col`} data-testid="transcription-card">
                  <div className={`${panelHead} flex-wrap gap-y-2`}>
                    <FileText className="w-4 h-4 text-[#00F0FF]" />
                    <h3 className="font-['Space_Grotesk'] text-sm font-semibold tracking-tight">Transcrição ao Vivo</h3>
                    {liveTranscript.length > 0 && (
                      <span className="font-['JetBrains_Mono'] text-xs text-slate-500">({liveTranscript.length})</span>
                    )}
                    <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" onClick={copyLiveTranscript} className="h-7 px-2 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white" data-testid="copy-transcript-btn">
                        {transcriptCopied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {transcriptCopied ? 'Copiado' : 'Copiar'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={downloadTranscript} className="h-7 px-2 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white" data-testid="download-transcript-btn">
                        <Download className="w-3 h-3 mr-1" /> TXT
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadTranscriptPDF()} className="h-7 px-2 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white" data-testid="download-transcript-pdf-btn">
                        <FileDown className="w-3 h-3 mr-1" /> PDF Original
                      </Button>
                      {session?.target_langs?.map(lang => (
                        <Button key={lang} size="sm" variant="outline" onClick={() => downloadTranscriptPDF(lang)} className="h-7 px-2 text-[11px] border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white" data-testid={`download-transcript-pdf-${lang}-btn`}>
                          <FileDown className="w-3 h-3 mr-1" /> PDF {lang.toUpperCase()}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-[420px] max-h-[calc(100vh-200px)] px-4 py-3" data-testid="live-transcript-list">
                    {liveTranscript.length === 0 ? (
                      <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center">
                        <div className={`w-14 h-14 rounded-full border flex items-center justify-center mb-4 ${isLive ? 'border-[#00F0FF]/40 bg-[#00F0FF]/5' : 'border-white/10 bg-white/[0.03]'}`}>
                          {isLive ? <Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" /> : <Mic className="w-6 h-6 text-slate-600" />}
                        </div>
                        <p className="font-['Inter'] text-sm text-slate-400 max-w-xs">
                          {isLive ? 'A aguardar fala… As transcrições aparecem aqui em tempo real.' : 'Inicie a captação para ver a transcrição em tempo real.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {liveTranscript.slice(-60).map((entry, idx) => (
                          <div key={idx} className="flex gap-3 text-sm py-1.5 border-b border-white/5 last:border-0">
                            <span className="font-['JetBrains_Mono'] text-[11px] text-slate-600 whitespace-nowrap pt-0.5">{entry.time}</span>
                            <span className="font-['Inter'] text-slate-200 leading-relaxed">{entry.text}</span>
                          </div>
                        ))}
                        <div ref={transcriptEndRef} />
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </main>
        </>
      )}

      {/* QR Modal */}
      {qrOpen && session && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setQrOpen(false)}>
          <div className="bg-[#0C1222] border border-white/10 rounded-xl p-6 w-full max-w-sm mx-4 shadow-2xl text-center" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <h3 className="font-['Space_Grotesk'] text-lg font-semibold tracking-tight mb-1">Acesso dos Participantes</h3>
            <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#00F0FF] tracking-[0.25em] mb-4">{session.code}</p>
            <div className="bg-white p-4 rounded-lg inline-block" data-testid="qr-code">
              <QRCodeSVG value={session.join_url} size={220} />
            </div>
            <p className="font-['Inter'] text-xs text-slate-400 mt-4 break-all" data-testid="join-url">{session.join_url}</p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => copyToClipboard(session.join_url)} className="flex-1 bg-[#00F0FF] hover:bg-[#33F3FF] text-[#050814] font-semibold h-9 text-sm">
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />} Copiar Link
              </Button>
              <Button variant="outline" onClick={() => setQrOpen(false)} className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 h-9 text-sm">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AV Overlay Popup */}
      {avOverlayOpen && session && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setAvOverlayOpen(false)}>
          <div className="bg-[#0C1222] border border-white/10 rounded-xl p-5 w-full max-w-md mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="av-overlay-popup">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-purple-400" />
                <h3 className="font-['Space_Grotesk'] text-base font-semibold tracking-tight">AV Overlay</h3>
                <span className="font-['Inter'] text-xs text-slate-500">Chroma Key</span>
              </div>
              <button onClick={() => setAvOverlayOpen(false)} className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-['Inter'] text-xs text-slate-400">Idioma:</span>
              <select
                value={avOverlayLang || (session.target_langs || [])[0] || ''}
                onChange={(e) => setAvOverlayLang(e.target.value)}
                className="bg-[#0A0F1E] border border-white/10 text-white text-xs rounded px-2 py-1 font-['Inter']"
                data-testid="av-overlay-lang-select"
              >
                {(session.target_langs || []).map((lang) => (
                  <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2.5">
              {[
                { key: 'green', label: 'Green Screen', color: 'bg-green-500' },
                { key: 'blue', label: 'Blue Screen', color: 'bg-blue-500' },
                { key: 'black', label: 'Black Clean', color: 'bg-slate-700' },
              ].map(({ key, label, color }) => {
                const lang = avOverlayLang || (session.target_langs || [])[0] || 'pt';
                const url = `${window.location.origin}/join/${session.code}?mode=overlay&bg=${key}&lang=${lang}`;
                return (
                  <div key={key} className="flex items-center gap-2 bg-[#0A0F1E] border border-white/10 rounded-lg px-3 py-2" data-testid={`av-overlay-${key}`}>
                    <div className={`w-3 h-3 rounded-sm ${color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <span className="font-['Inter'] text-xs text-slate-300">{label}</span>
                      <span className="font-['JetBrains_Mono'] text-[10px] text-slate-600 block truncate">{url}</span>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(url);
                        setAvCopied(key);
                        toast.success(`Link ${label} copiado!`);
                        setTimeout(() => setAvCopied(null), 2000);
                      }}
                      className="flex-shrink-0 p-1.5 rounded hover:bg-white/10 transition-colors"
                      data-testid={`av-overlay-copy-${key}`}
                    >
                      {avCopied === key ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="font-['Inter'] text-[10px] text-slate-600 text-center mt-3">OBS: Browser Source → colar URL → aplicar Chroma Key</p>
          </div>
        </div>
      )}
    </div>
  );
}
